import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getStats, getExpenses } from '../services/api';
import websocket from '../services/websocket';
import Icon from '../components/Icon';
import { exportStatsToExcel } from '../utils/exportUtils';
import DateRangePicker from '../components/DateRangePicker';
import PlanWidget from '../components/PlanWidget';
import HelpIcon from '../components/help/HelpIcon';
import { storage } from '../utils/storage';
import './Dashboard.css';

const STATUS_COLORS = {
  pending: '#FF9800',
  preparing: '#2196F3',
  ready: '#a0f487',
  delivered: '#8BC34A',
  cancelled: '#f44336',
};

function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [prevExpenses, setPrevExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [customRange, setCustomRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      let options = { period };

      if (period === 'custom') {
        if (!customRange.startDate || !customRange.endDate) {
          setLoading(false);
          return;
        }
        options = {
          period,
          startDate: customRange.startDate.toISOString().split('T')[0],
          endDate: customRange.endDate.toISOString().split('T')[0],
        };
      }

      // 1. Fetch Order Stats
      const data = await getStats(options);
      setStats(data);

      // 2. Fetch Expenses for current and previous period
      if (data && data.period) {
        const start = new Date(data.period.startDate);
        const end = new Date(data.period.endDate);
        const durationMs = end.getTime() - start.getTime() + 86400000; // +1 day inclusive
        const prevEnd = new Date(start.getTime() - 86400000);
        const prevStart = new Date(prevEnd.getTime() - durationMs + 86400000);

        const [expensesData, prevExpensesData] = await Promise.all([
          getExpenses({ startDate: data.period.startDate, endDate: data.period.endDate }),
          getExpenses({
            startDate: prevStart.toISOString().split('T')[0],
            endDate: prevEnd.toISOString().split('T')[0],
          }),
        ]);
        setExpenses(expensesData.expenses || []);
        setPrevExpenses(prevExpensesData.expenses || []);
      }
    } catch (error) {
      console.error('[Dashboard] Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  }, [period, customRange]);

  useEffect(() => {
    const restaurantId = storage.get('selectedRestaurantId');

    loadStats();

    // WebSocket handlers
    const handleNewOrder = () => {
      console.log('[Dashboard] Nuevo pedido - recargando stats');
      loadStats();
    };

    const handleOrderStatusChanged = () => {
      console.log('[Dashboard] Estado cambiado - recargando stats');
      loadStats();
    };

    if (restaurantId) {
      websocket.connect(restaurantId);
      websocket.on('NEW_ORDER', handleNewOrder);
      websocket.on('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
    }

    return () => {
      websocket.off('NEW_ORDER', handleNewOrder);
      websocket.off('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
    };
  }, [loadStats]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatChange = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getChangeClass = (value) => {
    if (value > 0) return 'change-positive';
    if (value < 0) return 'change-negative';
    return 'change-neutral';
  };

  if (loading && !stats) {
    return <div className="loading">{t('dashboard.loadingStats')}</div>;
  }

  // Calculate Financials
  const totalExpenses = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
  const prevTotalExpenses = prevExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
  const netProfit = (stats?.summary?.totalRevenue || 0) - totalExpenses;

  const prevRevenue =
    stats?.comparison?.revenueChange != null && stats.summary.totalRevenue
      ? stats.summary.totalRevenue / (1 + stats.comparison.revenueChange / 100)
      : 0;
  const prevNetProfit = prevRevenue - prevTotalExpenses;

  const expensesChange =
    prevTotalExpenses > 0 ? ((totalExpenses - prevTotalExpenses) / prevTotalExpenses) * 100 : null;
  const netProfitChange =
    prevNetProfit !== 0 ? ((netProfit - prevNetProfit) / Math.abs(prevNetProfit)) * 100 : null;

  // Prepare Data for Charts
  const statusData = stats
    ? [
        {
          name: t('orders.status.pending'),
          value: stats.byStatus.pending,
          color: STATUS_COLORS.pending,
        },
        {
          name: t('orders.status.preparing'),
          value: stats.byStatus.preparing,
          color: STATUS_COLORS.preparing,
        },
        { name: t('orders.status.ready'), value: stats.byStatus.ready, color: STATUS_COLORS.ready },
        {
          name: t('orders.status.delivered'),
          value: stats.byStatus.delivered,
          color: STATUS_COLORS.delivered,
        },
        {
          name: t('orders.status.cancelled'),
          value: stats.byStatus.cancelled,
          color: STATUS_COLORS.cancelled,
        },
      ].filter((d) => d.value > 0)
    : [];

  // Order Type data
  const ORDER_TYPE_COLORS = { dine_in: '#7e4fb9', takeaway: '#FF9800', delivery: '#2196F3' };
  const ORDER_TYPE_LABELS = {
    dine_in: t('orders.filters.dineIn', 'En mesa'),
    takeaway: t('orders.filters.takeaway', 'Para llevar'),
    delivery: t('orders.filters.delivery', 'Delivery'),
  };

  const orderTypeData = (stats?.byOrderType || [])
    .map((item) => ({
      name: ORDER_TYPE_LABELS[item.type] || item.type,
      orders: item.orders,
      revenue: item.revenue,
      color: ORDER_TYPE_COLORS[item.type] || '#9E9E9E',
    }))
    .filter((d) => d.orders > 0);

  // Expense Category data
  const EXPENSE_COLORS = ['#f44336', '#E91E63', '#9C27B0', '#673AB7', '#FF5722', '#795548'];

  const expenseCategoryData = (() => {
    const byCategory = {};
    expenses.forEach((exp) => {
      const cat = exp.categoryName || exp.categoryId || t('dashboard.noExpenseCategory');
      byCategory[cat] = (byCategory[cat] || 0) + parseFloat(exp.amount);
    });
    return Object.entries(byCategory)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount);
  })();

  const hourlyData = stats?.byHour?.filter((h) => h.orders > 0) || [];

  // Prepare Financial Comparison Data (Revenue vs Expenses)
  // Simple bar chart with 2 bars
  const financialData = [
    { name: t('dashboard.revenue'), amount: stats?.summary?.totalRevenue || 0, fill: '#7e4fb9' },
    { name: t('dashboard.totalExpenses'), amount: totalExpenses, fill: '#f44336' },
  ];

  return (
    <div className="dashboard">
      <div className="page-header">
        <div className="header-content">
          <h1>
            {t('dashboard.title')}
            <HelpIcon topicKey="dashboard.stats" placement="right" />
          </h1>
          <p>{t('dashboard.todaySummary')}</p>
        </div>

        <div className="period-selector">
          <button
            type="button"
            className="btn btn-secondary export-btn"
            onClick={() => exportStatsToExcel(stats, expenses, period, t, customRange)}
            disabled={!stats}
            title={t('dashboard.exportExcel')}
          >
            <Icon name="download" />
          </button>

          {period === 'custom' && (
            <DateRangePicker
              startDate={customRange.startDate}
              endDate={customRange.endDate}
              onChange={(field, date) => setCustomRange((prev) => ({ ...prev, [field]: date }))}
            />
          )}

          <button
            type="button"
            className={`period-btn ${period === 'today' ? 'active' : ''}`}
            onClick={() => setPeriod('today')}
          >
            {t('dashboard.periods.today')}
          </button>
          <button
            type="button"
            className={`period-btn ${period === 'yesterday' ? 'active' : ''}`}
            onClick={() => setPeriod('yesterday')}
          >
            {t('dashboard.periods.yesterday')}
          </button>
          <button
            type="button"
            className={`period-btn ${period === 'week' ? 'active' : ''}`}
            onClick={() => setPeriod('week')}
          >
            {t('dashboard.periods.week')}
          </button>
          <button
            type="button"
            className={`period-btn ${period === 'month' ? 'active' : ''}`}
            onClick={() => setPeriod('month')}
          >
            {t('dashboard.periods.month')}
          </button>
          <button
            type="button"
            className={`period-btn ${period === 'custom' ? 'active' : ''}`}
            onClick={() => setPeriod('custom')}
          >
            {t('dashboard.periods.custom')}
          </button>
        </div>
      </div>

      {/* Plan Widget */}
      <div className="dashboard-plan-widget">
        <PlanWidget />
      </div>

      {stats && (
        <>
          {/* Main Stats Cards - Row 1: Operations */}
          <div className="stats-grid">
            <div className="stat-card primary">
              <div className="stat-icon">
                <Icon name="payments" size="large" />
              </div>
              <div className="stat-info">
                <h2>{formatCurrency(stats.summary.totalRevenue)}</h2>
                <p>{t('dashboard.totalRevenue')}</p>
                <span className="stat-hint">{t('dashboard.revenueHint')}</span>
                <span className={`stat-change ${getChangeClass(stats.comparison.revenueChange)}`}>
                  {formatChange(stats.comparison.revenueChange)} vs {t('dashboard.previousPeriod')}
                </span>
              </div>
            </div>

            <div className="stat-card">
              <div
                className="stat-icon"
                style={{
                  backgroundColor:
                    totalExpenses > 0 ? 'var(--alert-error-bg)' : 'var(--alert-success-bg)',
                  color:
                    totalExpenses > 0 ? 'var(--alert-error-text)' : 'var(--alert-success-text)',
                }}
              >
                <Icon name="money_off" size="large" />
              </div>
              <div className="stat-info">
                <h2
                  style={{
                    color:
                      totalExpenses > 0 ? 'var(--alert-error-text)' : 'var(--alert-success-text)',
                  }}
                >
                  {formatCurrency(totalExpenses)}
                </h2>
                <p>{t('dashboard.totalExpenses')}</p>
                {expensesChange != null ? (
                  <span className={`stat-change ${getChangeClass(expensesChange * -1)}`}>
                    {formatChange(expensesChange)} vs {t('dashboard.previousPeriod')}
                  </span>
                ) : (
                  <span className="stat-change change-neutral">N/A</span>
                )}
              </div>
            </div>

            <div className="stat-card">
              <div
                className="stat-icon"
                style={{
                  backgroundColor:
                    netProfit >= 0 ? 'var(--alert-success-bg)' : 'var(--alert-error-bg)',
                  color: netProfit >= 0 ? 'var(--alert-success-text)' : 'var(--alert-error-text)',
                }}
              >
                <Icon name="account_balance_wallet" size="large" />
              </div>
              <div className="stat-info">
                <h2
                  style={{
                    color: netProfit >= 0 ? 'var(--alert-success-text)' : 'var(--alert-error-text)',
                  }}
                >
                  {formatCurrency(netProfit)}
                </h2>
                <p>{t('dashboard.netProfit')}</p>
                {netProfitChange != null ? (
                  <span className={`stat-change ${getChangeClass(netProfitChange)}`}>
                    {formatChange(netProfitChange)} vs {t('dashboard.previousPeriod')}
                  </span>
                ) : (
                  <span className="stat-change change-neutral">N/A</span>
                )}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Icon name="receipt_long" size="large" />
              </div>
              <div className="stat-info">
                <h2>{stats.summary.totalOrders}</h2>
                <p>{t('dashboard.totalOrders')}</p>
                <span className={`stat-change ${getChangeClass(stats.comparison.ordersChange)}`}>
                  {formatChange(stats.comparison.ordersChange)} vs {t('dashboard.previousPeriod')}
                </span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Icon name="confirmation_number" size="large" />
              </div>
              <div className="stat-info">
                <h2>{formatCurrency(stats.summary.averageTicket)}</h2>
                <p>{t('dashboard.averageTicket')}</p>
                <span className="stat-hint">{t('dashboard.averageTicketHint')}</span>
                <span className={`stat-change ${getChangeClass(stats.comparison.ticketChange)}`}>
                  {formatChange(stats.comparison.ticketChange)} vs {t('dashboard.previousPeriod')}
                </span>
              </div>
            </div>
          </div>

          {/* Revenue Breakdown & Financial Chart */}
          <div className="stats-row">
            <div className="card breakdown-card">
              <h2>{t('dashboard.revenueBreakdown')}</h2>
              <div className="breakdown-items">
                <div className="breakdown-item">
                  <span className="breakdown-label">{t('dashboard.subtotal')}</span>
                  <span className="breakdown-value">
                    {formatCurrency(stats.summary.totalSubtotal)}
                  </span>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-label">{t('dashboard.serviceCharge')} (10%)</span>
                  <span className="breakdown-value">
                    {formatCurrency(stats.summary.totalServiceCharge)}
                  </span>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-label">{t('dashboard.tax')} (21%)</span>
                  <span className="breakdown-value">{formatCurrency(stats.summary.totalTax)}</span>
                </div>
                <div className="breakdown-item total">
                  <span className="breakdown-label">{t('dashboard.total')}</span>
                  <span className="breakdown-value">
                    {formatCurrency(stats.summary.totalRevenue)}
                  </span>
                </div>
                {/* Financial Summary in Breakdown */}
                <div className="breakdown-divider"></div>
                <div className="breakdown-item">
                  <span className="breakdown-label" style={{ color: '#d32f2f' }}>
                    {t('dashboard.operatingExpenses')}
                  </span>
                  <span className="breakdown-value" style={{ color: '#d32f2f' }}>
                    {formatCurrency(totalExpenses)}
                  </span>
                </div>
                <div className="breakdown-item total">
                  <span className="breakdown-label">{t('dashboard.netProfit')}</span>
                  <span
                    className="breakdown-value"
                    style={{ color: netProfit >= 0 ? '#2e7d32' : '#d32f2f' }}
                  >
                    {formatCurrency(netProfit)}
                  </span>
                </div>
              </div>
            </div>

            {/* Financial Chart */}
            <div className="card chart-card">
              <h2>{t('dashboard.revenueVsExpenses')}</h2>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={financialData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(val) => formatCurrency(val)} />
                  <YAxis dataKey="name" type="category" width={80} />
                  <Tooltip separator=": " formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="amount" radius={[0, 10, 10, 0]}>
                    {financialData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Detailed Charts Row */}
          <div className="stats-row">
            <div className="card chart-card full-width">
              <h2>{t('dashboard.salesByHour')}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" tickFormatter={(hour) => `${hour}:00`} />
                  <YAxis yAxisId="left" orientation="left" stroke="#7e4fb9" />
                  <YAxis yAxisId="right" orientation="right" stroke="#2196F3" />
                  <Tooltip
                    separator=": "
                    formatter={(value, _name, props) =>
                      props.dataKey === 'revenue' ? formatCurrency(value) : value
                    }
                    labelFormatter={(hour) => `${hour}:00 - ${hour}:59`}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="revenue"
                    fill="#7e4fb9"
                    name={t('dashboard.revenue')}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="orders"
                    fill="#2196F3"
                    name={t('dashboard.orders')}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Order Status & Order Type PieCharts */}
          <div className="stats-row">
            <div className="card chart-card">
              <h2>{t('dashboard.ordersByStatus')}</h2>
              {statusData.length > 0 ? (
                <div className="pie-container">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`status-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip separator=": " />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="no-data">{t('dashboard.noData')}</p>
              )}
            </div>

            <div className="card chart-card">
              <h2>{t('dashboard.ordersByType')}</h2>
              {orderTypeData.length > 0 ? (
                <div className="pie-container">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={orderTypeData}
                        dataKey="orders"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, orders }) => `${name}: ${orders}`}
                      >
                        {orderTypeData.map((entry, index) => (
                          <Cell key={`type-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        separator=": "
                        formatter={(value, _name, props) =>
                          props.dataKey === 'revenue' ? formatCurrency(value) : value
                        }
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="no-data">{t('dashboard.noData')}</p>
              )}
            </div>
          </div>

          {/* Expense Categories PieChart */}
          <div className="stats-row">
            <div className="card chart-card">
              <h2>{t('dashboard.expensesByCategory')}</h2>
              {expenseCategoryData.length > 0 ? (
                <div className="pie-container">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={expenseCategoryData}
                        dataKey="amount"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, amount }) => `${name}: ${formatCurrency(amount)}`}
                      >
                        {expenseCategoryData.map((entry, index) => (
                          <Cell
                            key={`expense-${index}`}
                            fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip separator=": " formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="no-data">{t('dashboard.noData')}</p>
              )}
            </div>
          </div>

          {/* Daily Trend (for week/month views) */}
          {stats.byDay && stats.byDay.length > 1 && (
            <div className="card chart-card full-width">
              <h2>{t('dashboard.dailyTrend')}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.byDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      return `${d.getDate()}/${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis yAxisId="left" orientation="left" stroke="#7e4fb9" />
                  <YAxis yAxisId="right" orientation="right" stroke="#2196F3" />
                  <Tooltip
                    separator=": "
                    formatter={(value, _name, props) =>
                      props.dataKey === 'revenue' ? formatCurrency(value) : value
                    }
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#7e4fb9"
                    strokeWidth={2}
                    name={t('dashboard.revenue')}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="orders"
                    stroke="#2196F3"
                    strokeWidth={2}
                    name={t('dashboard.orders')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Two Column Layout: Top Products + Sales by Table */}
          <div className="stats-row">
            {/* Top Products */}
            <div className="card list-card">
              <h2>{t('dashboard.topProducts')}</h2>
              {stats.topProducts && stats.topProducts.length > 0 ? (
                <div className="top-products-list">
                  {stats.topProducts.slice(0, 5).map((product, index) => (
                    <div key={product.itemId || index} className="top-product-item">
                      <span className="product-rank">#{index + 1}</span>
                      <div className="product-info">
                        <span className="product-name">{product.name}</span>
                        <span className="product-stats">
                          {product.quantity} {t('dashboard.unitsSold')} •{' '}
                          {formatCurrency(product.revenue)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">{t('dashboard.noProductsSold')}</p>
              )}
            </div>

            {/* Sales by Table */}
            <div className="card list-card">
              <h2>{t('dashboard.salesByTable')}</h2>
              {stats.byTable && stats.byTable.length > 0 ? (
                <div className="table-sales-list">
                  {stats.byTable.slice(0, 5).map((table, index) => (
                    <div key={table.table} className="table-sales-item">
                      <span className="table-number">
                        <Icon name="table_restaurant" size="small" />
                        {t('dashboard.table')} {table.table}
                      </span>
                      <div className="table-stats">
                        <span>{t('dashboard.ordersCount', { count: table.orders })}</span>
                        <span className="table-revenue">{formatCurrency(table.revenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">{t('dashboard.noTableSales')}</p>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h2>
              {t('dashboard.quickActions')}
              <HelpIcon topicKey="dashboard.quickActions" placement="right" />
            </h2>
            <div className="quick-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => (window.location.href = '/orders')}
              >
                <Icon name="receipt_long" /> {t('dashboard.viewOrders')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => (window.location.href = '/menu')}
              >
                <Icon name="restaurant_menu" /> {t('dashboard.manageMenu')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => (window.location.href = '/tables')}
              >
                <Icon name="table_restaurant" /> {t('dashboard.manageTables')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
